import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const CODE_POOL = [
  '0xFF3A1C', 'null', 'ERR::0x4F2', 'EXEC_INIT',
  '01101001', '> run ai.sh', 'MONSTER.exe',
  '0xDEAD', 'for(;;){}', 'buf[256]', 'AI_CORE_v2',
  'render()', '!malloc()', '0b10110111', 'sys.override',
  'ACCESS::OK', 'SIGNAL_LOST', 'reboot --force',
  'glitch.init()', '> sudo su', 'kernel panic',
];

interface CodeFragment {
  id: number;
  text: string;
  x: number;
  y: number;
  color: string;
  size: number;
  delay: number;
}

const FRAG_POSITIONS: { x: number; y: number }[] = [
  { x: 5, y: 12 }, { x: 78, y: 8 }, { x: 3, y: 45 },
  { x: 82, y: 40 }, { x: 10, y: 75 }, { x: 72, y: 72 },
  { x: 38, y: 4 }, { x: 42, y: 88 }, { x: 55, y: 18 },
  { x: 22, y: 55 }, { x: 65, y: 55 }, { x: 30, y: 82 },
];

export default function HeroSection() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isGlitching, setIsGlitching] = useState(false);
  const [codeFragments, setCodeFragments] = useState<CodeFragment[]>([]);
  const [scanProgress, setScanProgress] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', resize);

    const particles: { x: number; y: number; size: number; color: string; vx: number; vy: number; alpha: number }[] = [];
    const colors = ['#00ff41', '#00d9ff', '#ff006e', '#a855f7'];

    for (let i = 0; i < 120; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        size: Math.random() * 3 + 1,
        color: colors[Math.floor(Math.random() * colors.length)],
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        alpha: Math.random() * 0.6 + 0.1,
      });
    }

    let animId: number;
    let scanY = 0;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      scanY = (scanY + 1.5) % canvas.height;
      const grad = ctx.createLinearGradient(0, scanY - 60, 0, scanY + 60);
      grad.addColorStop(0, 'rgba(0,255,65,0)');
      grad.addColorStop(0.5, 'rgba(0,255,65,0.04)');
      grad.addColorStop(1, 'rgba(0,255,65,0)');
      ctx.fillStyle = grad;
      ctx.fillRect(0, scanY - 60, canvas.width, 120);

      particles.forEach(p => {
        ctx.save();
        ctx.globalAlpha = p.alpha;
        ctx.fillStyle = p.color;
        ctx.fillRect(p.x, p.y, p.size, p.size);
        ctx.restore();
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
      });

      animId = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  const scrollDown = () => {
    const el = document.getElementById('about');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleLogoClick = () => {
    if (isGlitching) return;

    const fragColors = ['#00ff41', '#00d9ff', '#00ff41', '#00ff41', '#00d9ff'];
    const shuffled = [...CODE_POOL].sort(() => Math.random() - 0.5);
    const frags: CodeFragment[] = FRAG_POSITIONS.map((pos, i) => ({
      id: i,
      text: shuffled[i % shuffled.length],
      x: pos.x,
      y: pos.y,
      color: fragColors[i % fragColors.length],
      size: 10 + Math.floor(Math.random() * 4),
      delay: i * 30,
    }));
    setCodeFragments(frags);
    setIsGlitching(true);
    setScanProgress(0);

    const start = performance.now();
    const duration = 500;
    const tick = (now: number) => {
      const p = Math.min((now - start) / duration, 1);
      setScanProgress(p);
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);

    setTimeout(() => {
      setIsGlitching(false);
      setCodeFragments([]);
      setScanProgress(0);
      const el = document.getElementById('about');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 500);
  };

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-[#060010]">
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />

      {/* Grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: `repeating-linear-gradient(0deg, #00ff41 0px, #00ff41 1px, transparent 1px, transparent 40px),
                            repeating-linear-gradient(90deg, #00ff41 0px, #00ff41 1px, transparent 1px, transparent 40px)`,
        }}
      />

      {/* Radial glow center */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div
          className="w-[600px] h-[600px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(0,255,65,0.07) 0%, rgba(0,217,255,0.04) 40%, transparent 70%)',
          }}
        />
      </div>

      {/* Glitch overlay */}
      {isGlitching && (
        <div className="absolute inset-0 z-20 pointer-events-none overflow-hidden">
          <div
            className="absolute inset-0"
            style={{
              background: 'repeating-linear-gradient(to bottom, transparent 0px, transparent 3px, rgba(0,255,65,0.06) 3px, rgba(0,255,65,0.06) 4px)',
            }}
          />
          <div
            className="absolute left-0 right-0 h-[3px]"
            style={{
              top: `${scanProgress * 100}%`,
              background: 'linear-gradient(90deg, transparent, rgba(0,255,65,0.9), rgba(0,217,255,0.7), transparent)',
              boxShadow: '0 0 12px rgba(0,255,65,0.6), 0 0 30px rgba(0,255,65,0.2)',
            }}
          />
          <div
            className="absolute left-0 right-0"
            style={{
              top: 0,
              height: `${scanProgress * 100}%`,
              background: 'linear-gradient(to bottom, transparent 80%, rgba(0,255,65,0.04))',
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url(https://static.readdy.ai/image/792df796680eec38aa20c46d1adacb8b/8a76a6dd2c1eb78247ce2c779019b5d9.png)`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundSize: '256px 256px',
              mixBlendMode: 'screen',
              opacity: 0.22,
              animation: 'cyanShift 0.1s steps(1) infinite',
            }}
          />
          {codeFragments.map(f => (
            <div
              key={f.id}
              className="absolute font-['JetBrains_Mono'] whitespace-nowrap"
              style={{
                left: `${f.x}%`,
                top: `${f.y}%`,
                color: f.color,
                fontSize: `${f.size}px`,
                opacity: 0,
                animation: `codeAppear 0.5s steps(1) forwards`,
                animationDelay: `${f.delay}ms`,
                textShadow: `0 0 8px ${f.color}`,
                letterSpacing: '0.05em',
              }}
            >
              {f.text}
            </div>
          ))}
          <div
            className="absolute left-1/2 top-[58%] -translate-x-1/2 font-['JetBrains_Mono'] text-[#00ff41] text-xs tracking-[0.3em] whitespace-nowrap"
            style={{
              textShadow: '0 0 10px rgba(0,255,65,0.8)',
              animation: 'terminalBlink 0.12s steps(1) infinite',
            }}
          >
            SCANNING... {Math.floor(scanProgress * 100)}%
          </div>
        </div>
      )}

      {/* Center content */}
      <div className="relative z-10 flex flex-col items-center justify-center text-center select-none">
        <div className="relative mb-8 group">
          <div
            className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-700"
            style={{
              background: 'radial-gradient(circle, rgba(0,255,65,0.25) 0%, transparent 70%)',
              filter: 'blur(20px)',
            }}
          />
          <img
            src="https://static.readdy.ai/image/792df796680eec38aa20c46d1adacb8b/8a76a6dd2c1eb78247ce2c779019b5d9.png"
            alt="Pixel Monster Logo"
            className="w-64 h-64 object-contain drop-shadow-2xl cursor-pointer"
            onClick={handleLogoClick}
            style={{
              filter: isGlitching
                ? 'drop-shadow(0 0 30px rgba(0,255,65,0.9)) drop-shadow(0 0 50px rgba(0,217,255,0.5)) brightness(1.4) contrast(1.1)'
                : 'drop-shadow(0 0 30px rgba(0,255,65,0.3)) drop-shadow(0 0 60px rgba(0,217,255,0.15))',
              animation: isGlitching
                ? 'logoGlitch 0.09s steps(1) infinite'
                : 'logoFloat 5s ease-in-out infinite',
              transition: 'filter 0.08s',
            }}
          />
        </div>

        <div className="mb-3">
          <span
            className="text-5xl font-bold font-['Space_Grotesk'] tracking-widest"
            style={{
              background: 'linear-gradient(90deg, #00ff41, #00d9ff, #ff006e, #00ff41)',
              backgroundSize: '300% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: isGlitching
                ? 'textFlicker 0.08s steps(1) infinite'
                : 'gradientShift 4s linear infinite',
            }}
          >
            PIXEL MONSTER
          </span>
        </div>

        <p className="text-[#00ff41] font-['JetBrains_Mono'] text-sm tracking-[0.3em] opacity-70">
          {t('hero.tagline')}
        </p>
      </div>

      {/* Scroll down arrow */}
      <button
        onClick={scrollDown}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 cursor-pointer group"
        aria-label={t('hero.scrollLabel')}
      >
        <span className="text-[#00ff41]/40 font-['JetBrains_Mono'] text-[10px] tracking-[0.2em] group-hover:text-[#00ff41]/80 transition-colors duration-300">
          SCROLL
        </span>
        <div className="flex flex-col items-center gap-1" style={{ animation: 'arrowBounce 1.8s ease-in-out infinite' }}>
          <div className="w-[1px] h-8 bg-gradient-to-b from-transparent to-[#00ff41]/60" />
          <div
            className="w-3 h-3 border-r-2 border-b-2 border-[#00ff41]/60 group-hover:border-[#00ff41] transition-colors duration-300"
            style={{ transform: 'rotate(45deg)' }}
          />
        </div>
      </button>

      <style>{`
        @keyframes logoFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-14px); }
        }
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          100% { background-position: 300% 50%; }
        }
        @keyframes arrowBounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(8px); }
        }
        @keyframes logoGlitch {
          0%   { transform: translate(0, 0) skewX(0deg); }
          12%  { transform: translate(-7px, 3px) skewX(-5deg); }
          25%  { transform: translate(6px, -4px) skewX(4deg); }
          37%  { transform: translate(-5px, -5px) skewX(6deg); }
          50%  { transform: translate(8px, 2px) skewX(-5deg); }
          62%  { transform: translate(-4px, 6px) skewX(3deg); }
          75%  { transform: translate(5px, -6px) skewX(-4deg); }
          87%  { transform: translate(-6px, 1px) skewX(5deg); }
          100% { transform: translate(0, 0) skewX(0deg); }
        }
        @keyframes textFlicker {
          0%   { opacity: 1; }
          20%  { opacity: 0.5; }
          40%  { opacity: 1; }
          60%  { opacity: 0.7; }
          80%  { opacity: 0.3; }
          100% { opacity: 1; }
        }
        @keyframes cyanShift {
          0%   { transform: translate(-6px, 0); filter: hue-rotate(0deg); }
          25%  { transform: translate(5px, -3px); filter: hue-rotate(30deg); }
          50%  { transform: translate(-4px, 4px); filter: hue-rotate(0deg); }
          75%  { transform: translate(7px, -2px); filter: hue-rotate(-20deg); }
          100% { transform: translate(-6px, 0); filter: hue-rotate(0deg); }
        }
        @keyframes codeAppear {
          0%   { opacity: 0; transform: translateY(4px); }
          20%  { opacity: 0.9; transform: translateY(0); }
          80%  { opacity: 0.9; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(-4px); }
        }
        @keyframes terminalBlink {
          0%   { opacity: 1; }
          49% { opacity: 1; }
          50%  { opacity: 0.2; }
          100% { opacity: 1; }
        }
      `}</style>
    </section>
  );
}
